import React, { useRef, useMemo } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Environment } from "@react-three/drei";
import { EffectComposer, Bloom, Glitch } from "@react-three/postprocessing";
import * as THREE from "three";

// Soft ASMR Easing
const ease = (t) => t * t * (3 - 2 * t);

// Jelly wobble function
const wobble = (i, time) =>
  Math.sin(i * 0.07 + time * 0.9) * 0.015 +
  Math.cos(i * 0.11 + time * 0.4) * 0.015;

// Shapes
const toSphere = (d) => d.clone();
const toCube = (d) => {
  const m = Math.max(Math.abs(d.x), Math.abs(d.y), Math.abs(d.z));
  return new THREE.Vector3(d.x / m, d.y / m, d.z / m);
};
const toTorus = (d) => {
  const t = Math.atan2(d.z, d.x);
  const p = Math.acos(THREE.MathUtils.clamp(d.y, -1, 1));
  const R = 0.8,
    r = 0.35;
  return new THREE.Vector3(
    (R + r * Math.cos(p)) * Math.cos(t),
    r * Math.sin(p),
    (R + r * Math.cos(p)) * Math.sin(t)
  );
};
const toBlob = (d) =>
  d.clone().multiply(
    new THREE.Vector3(
      1 + 0.3 * Math.sin(d.x * 5),
      1 + 0.3 * Math.cos(d.y * 5),
      1 + 0.3 * Math.sin(d.z * 5)
    )
  );

function AuraParticles() {
  const ref = useRef();
  const count = 200;

  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3 + 0] = (Math.random() - 0.5) * 3;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 3;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 3;
    }
    return arr;
  }, []);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    ref.current.rotation.y = t * 0.03;
    ref.current.rotation.x = t * 0.015;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial size={0.035} color="#aac8ff" transparent opacity={0.4} />
    </points>
  );
}

function MorphingMesh() {
  const mesh = useRef();
  const { clock } = useThree();

  const { basePositions, sphere, cube, torus, blob } = useMemo(() => {
    const geo = new THREE.IcosahedronGeometry(1, 5);
    const count = geo.attributes.position.count;

    const base = new Float32Array(count * 3);
    const s = new Float32Array(count * 3);
    const c = new Float32Array(count * 3);
    const t = new Float32Array(count * 3);
    const b = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      const d = new THREE.Vector3()
        .fromBufferAttribute(geo.attributes.position, i)
        .normalize();

      base.set([d.x, d.y, d.z], i * 3);
      s.set(Object.values(toSphere(d)), i * 3);
      c.set(Object.values(toCube(d)), i * 3);
      t.set(Object.values(toTorus(d)), i * 3);
      b.set(Object.values(toBlob(d)), i * 3);
    }

    return { basePositions: base, sphere: s, cube: c, torus: t, blob: b };
  }, []);

  const shapes = ["sphere", "cube", "torus", "blob"];
  const maps = { sphere, cube, torus, blob };

  const state = useRef({ curr: 0, next: 1, t: 0, duration: 5 });

  const buffer = useMemo(
    () =>
      new THREE.BufferGeometry().setAttribute(
        "position",
        new THREE.BufferAttribute(basePositions, 3)
      ),
    [basePositions]
  );

  useFrame((_, delta) => {
    const st = state.current;
    const pos = buffer.attributes.position;
    const time = clock.getElapsedTime();

    st.t += delta / st.duration;
    const p = ease(Math.min(st.t, 1));

    const A = maps[shapes[st.curr]];
    const B = maps[shapes[st.next]];

    for (let i = 0; i < pos.count; i++) {
      const i3 = i * 3;
      const jelly = wobble(i, time);

      pos.array[i3] = THREE.MathUtils.lerp(A[i3], B[i3], p) + jelly;
      pos.array[i3 + 1] = THREE.MathUtils.lerp(A[i3 + 1], B[i3 + 1], p) + jelly;
      pos.array[i3 + 2] = THREE.MathUtils.lerp(A[i3 + 2], B[i3 + 2], p) + jelly;
    }

    pos.needsUpdate = true;

    // Breathing scale animation
    const s = 1 + Math.sin(time * 0.9) * 0.05;
    mesh.current.scale.set(s, s, s);

    // Slow rotation
    mesh.current.rotation.x += 0.001;
    mesh.current.rotation.y += 0.0025;

    if (st.t >= 1) {
      st.t = 0;
      st.curr = st.next;
      st.next = (st.next + 1) % shapes.length;
    }
  });

  return (
    <mesh ref={mesh} geometry={buffer}>
      <meshStandardMaterial
        color="#bcdfff"
        roughness={0.1}
        metalness={0.3}
        emissive="#7ba7ff"
        emissiveIntensity={0.6}
      />
    </mesh>
  );
}

export default function ASMRPremiuMorph() {
  return (
    <div style={{ width: "100%", height: "100vh", background: "#080b12" }}>
      <Canvas camera={{ position: [0, 0, 4], fov: 50 }}>
        {/* Soft ASMR lights */}
        <ambientLight intensity={0.4} />
        <pointLight intensity={1} position={[4, 4, 6]} color="#bcd4ff" />
        <pointLight intensity={0.6} position={[-3, -2, -6]} color="#ffd4ea" />

        {/* Background aura */}
        <AuraParticles />

        {/* Morphing object */}
        <MorphingMesh />

        {/* Soft HDR lighting */}
        <Environment preset="dawn" />

        {/* Postprocessing magic */}
        <EffectComposer>
          <Bloom
            intensity={1.8}
            luminanceThreshold={0}
            luminanceSmoothing={0.6}
          />
        </EffectComposer>

        <OrbitControls enablePan={false} enableZoom={false} />
      </Canvas>
    </div>
  );
}
